// SHCT-582 — PRD 21 v2.14 §7.4 **P-3** 「우회 클릭 차단」, the L2 runtime layer.
//
// §7.4 splits the write guard into two levels on purpose. P-2 is the L1 UI guard: the button is
// `disabled` and carries the tooltip. P-3 is what happens when that guard is *bypassed* — the
// operator strips `disabled` in devtools and clicks — and it is not silent: 토스트(error) with the
// P-2 wording, 이벤트 취소. QC's re-test (2026-08-26, item 4) found nothing happened at all.
//
// The prototype implements this as a capture-phase click interceptor (`components.js`); the real
// admin web never had one, so a bypassed click ran the handler as if the permission existed. The
// server still refuses the write, but a refusal that only arrives as a failed request is not what
// §7.4 describes, and on a screen whose action is local-first (a draft edit, a reorder) there is no
// request to refuse.
//
// Why the document, in capture phase: React 18 attaches its listeners to the root container, which
// is a descendant of `document`, so a capture listener here runs first and
// `stopImmediatePropagation` keeps the click away from every React handler on the path — not just
// the one on the button.
//
// Nothing fires for an ordinary READ operator: a genuinely `disabled` <button> dispatches no click
// event at all. This listener only ever sees a control whose `disabled` was removed, which is
// exactly the case §7.4 assigns to P-3.

import { toast } from '@/components/feedback/useToast';
import { i18n } from '@/shared/i18n';
import { PERM_DENIED_ATTR } from '@/shared/hooks/useWriteGuard';

const DENIED_SELECTOR = `[${PERM_DENIED_ATTR}="true"]`;

let installed = false;

function onCaptureClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const guarded = target.closest(DENIED_SELECTOR);
  if (!guarded) return;

  // A `readOnly` field is refused by P-2 the same way a disabled button is, but it stays focusable
  // on purpose — the operator can still read and select its value. Clicking one is ordinary use,
  // not the devtools bypass P-3 describes, and cancelling it would take reading away too.
  if ((guarded as HTMLInputElement | HTMLTextAreaElement).readOnly === true) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  toast.error(i18n.t('gate.noPermission.action', { ns: 'common' }));
}

/**
 * Installs the P-3 interceptor. Idempotent — AuthProvider mounts twice under StrictMode, and two
 * listeners would mean two toasts for one click.
 */
export function installPermissionRuntimeGuard(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('click', onCaptureClick, true);
}

/** Test seam. */
export function uninstallPermissionRuntimeGuard(): void {
  if (!installed) return;
  installed = false;
  document.removeEventListener('click', onCaptureClick, true);
}
